import { useState, useEffect } from "react";

//Unform e componentes
import CardDashboard from "@/frontend/componentes/CardDashboardNovo";
import GridCards from "@/frontend/componentes/GridCards";
import SpinnerLoading from "@/frontend/componentes/Spinner";

//Common
import {
  CODIGO_CONTA_RECEITA,
  CODIGO_GRUPO_CONTA_RECEITA,
  SIGLA_FORMA_PAGAMENTO,
  SITUACAO_PAGAMENTO,
  TIPO_LANCAMENTO_CONTRATO,
  TIPO_SOLICITACAO,
} from "@/common/constantes";
import { fn_formatarNumero } from "@/common/utils/util";
import { fn_getDataAtual } from "@/common/utils/date";

//Util
import { MENSAGENS_ERRO, showMensagemErro } from "@/frontend/utils/mensagens";
import {
  fn_getDataUltimoVencimentoAtrasado,
  fn_getPeriodoAtrasadosReceita,
  fn_getPeriodoMesAnoVencimento,
  fn_isGrupoContaDashboardReceita,
} from "@/frontend/utils/siig/dashboard";

//API
import { api_getCountPagamento } from "@/frontend/services/api/api_others";
import { api_listar, ROTA_LISTAR } from "@/frontend/services/api/api_list";

//Telas
import TDashboard_detalhe, { COLUNA_DETALHE_CARD } from "./dashboard_detalhe";

/*
 * Cards do dashboard de receitas. Os filtros ficam na tela do dashboard (dashboard.jsx), que informa
 * os filtros confirmados e a chave da consulta (a cada consulta a chave muda).
 * @filtros: { mes_ano_inicio, mes_ano_fim, ids_area, codigo_grupo_conta } (ids_area: ids das áreas separados por vírgula;
 *           codigo_grupo_conta: um grupo ou os dois separados por vírgula, conforme os checkboxes marcados)
 * OBS: Todos os cards consideram só as receitas com o tipo lançamento do contrato Fluxo de caixa ou DRE e Fluxo de caixa.
 * OBS: Os valores dos cards são os valores rateados para as contas do grupo (ou para a conta, no card da conta),
 *      e não o valor do pagamento, pois o pagamento pode ter contas de outros grupos no rateio.
 * @datasFeriados: datas (DD/MM/YYYY) dos feriados que afetam pagamentos, para o vencimento atrasado (posição do dia)
 * Spec: documentos/specs/2026-10-24-dashboard-receitas.md
 */
function TDashboard_rec({ filtros, keyConsulta, datasFeriados }) {
  //### USE STATE ###
  const [infCards, setInfCards] = useState(null);

  //Card selecionado para apresentar o detalhamento dos pagamentos
  const [cardDetalhe, setCardDetalhe] = useState(null);

  const [loading, setLoading] = useState(false);

  //### VARIÁVEIS AUXILIARES ###
  const CARDS_POR_LINHA = 3;

  //### USE EFFECT ###
  //Listagem dos dados do dashboard
  useEffect(() => {
    if (keyConsulta > 0) {
      //Carrega os dados do dashboard
      async function loadDashboard() {
        try {
          setLoading(true);

          //Solicitações de receita aprovadas, das áreas e do grupo da conta, com o tipo lançamento do contrato
          //Fluxo de caixa ou DRE e Fluxo de caixa (a receita sem contrato não entra)
          const tiposLancamento = `${TIPO_LANCAMENTO_CONTRATO.FLUXO_CAIXA},${TIPO_LANCAMENTO_CONTRATO.DRE_FLUXO_CAIXA}`;
          const filtrosSolicitacao = `solicitacao_id_area=${filtros.ids_area}&solicitacao_tipo=${TIPO_SOLICITACAO.RECEITA}&codigo_grupo_conta=${filtros.codigo_grupo_conta}&tipo_lancamento_contrato=${tiposLancamento}&aprovacao_pendente=N`;
          //Pagamentos não cancelados
          const filtrosReceita = `${filtrosSolicitacao}&situacao=not${SITUACAO_PAGAMENTO.CANCELADO}`;

          //Período do vencimento: do primeiro dia do mês/ano inicial ao último dia do mês/ano final
          const periodo = fn_getPeriodoMesAnoVencimento(
            filtros.mes_ano_inicio,
            filtros.mes_ano_fim,
          );

          //Recebidos: com a data do pagamento (o oposto dos atrasados; a data do pagamento só existe com a posse)
          const filtrosRecebidos = `${filtrosReceita}&data_pagamento=notnull`;
          const recebidos = await api_getCountPagamento(
            periodo.data_inicio,
            periodo.data_fim,
            filtrosRecebidos,
          );

          //Total a receber: como os recebidos, com ou sem a data do pagamento
          const totalReceber = await api_getCountPagamento(
            periodo.data_inicio,
            periodo.data_fim,
            filtrosReceita,
          );

          //Atrasados: sem a data do pagamento e com o vencimento já passado (até o dia anterior ao início da posição do dia)
          const filtrosAtrasados = `${filtrosReceita}&data_pagamento=null`;
          const periodoAtrasados = fn_getPeriodoAtrasadosReceita(
            filtros.mes_ano_inicio,
            filtros.mes_ano_fim,
            fn_getDataAtual(),
            datasFeriados || [],
          );
          //Sem vencimento atrasado no período (ex: período futuro), não consulta
          const atrasados = periodoAtrasados
            ? await api_getCountPagamento(
                periodoAtrasados.data_inicio,
                periodoAtrasados.data_fim,
                filtrosAtrasados,
              )
            : { count: 0, total: 0, count_solicitacoes: 0 };

          //Atrasados total: a mesma consulta do Atrasados do período, sem o período escolhido no filtro:
          //todos os vencimentos já atrasados (até o dia anterior ao início da posição do dia)
          const dataFimAtrasadosTotal = fn_getDataUltimoVencimentoAtrasado(
            fn_getDataAtual(),
            datasFeriados || [],
          );
          const atrasadosTotal = await api_getCountPagamento(
            null,
            dataFimAtrasadosTotal,
            filtrosAtrasados,
          );

          //Conta: pagamentos com posse com a conta no rateio, com o valor rateado para ela
          //(só com Outras receitas operacionais marcado, pois a conta é desse grupo)
          let conta = null;
          if (
            fn_isGrupoContaDashboardReceita(
              filtros.codigo_grupo_conta,
              CODIGO_GRUPO_CONTA_RECEITA.OUTRAS_RECEITAS_OPERACIONAIS,
            )
          ) {
            const codigoConta = CODIGO_CONTA_RECEITA.RECEITAS_CONTA_CORRENTE;
            const [contaCadastro] = await api_listar(
              ROTA_LISTAR.CONTA,
              `codigo=${codigoConta}`,
            );

            const filtrosConta = `${filtrosReceita}&data_posse=notnull&codigo_conta_rateio=${codigoConta}`;
            const valorConta = await api_getCountPagamento(
              periodo.data_inicio,
              periodo.data_fim,
              filtrosConta,
            );

            conta = {
              //Título do card: o nome da conta no cadastro
              nome: contaCadastro?.nome || `Conta ${codigoConta}`,
              count: valorConta.count,
              total: valorConta.total,
              count_solicitacoes: valorConta.count_solicitacoes,
              filtros: filtrosConta,
              data_inicio: periodo.data_inicio,
              data_fim: periodo.data_fim,
            };
          }

          //Forma de pagamento: pagamentos com posse nessa forma de pagamento, das receitas do grupo, com o valor rateado
          //para as contas do grupo (como os demais cards). Apresentado em qualquer grupo da conta
          const siglaForma = SIGLA_FORMA_PAGAMENTO.CONTA_CORRENTE_FORNECEDOR;
          const [formaCadastro] = await api_listar(
            ROTA_LISTAR.FORMA_PAGAMENTO,
            `sigla=${siglaForma}`,
          );
          const filtrosForma = `${filtrosReceita}&data_posse=notnull&id_forma_pagamento=${formaCadastro?.id}`;
          //Sem a forma de pagamento no cadastro, o card fica zerado sem consultar (sem o id, a consulta traria todas as formas)
          const valorForma = formaCadastro
            ? await api_getCountPagamento(
                periodo.data_inicio,
                periodo.data_fim,
                filtrosForma,
              )
            : { count: 0, total: 0, count_solicitacoes: 0 };

          const forma = {
            //Título do card: o nome da forma de pagamento no cadastro
            nome: formaCadastro?.nome || `Forma de pagamento ${siglaForma}`,
            count: valorForma.count,
            total: valorForma.total,
            count_solicitacoes: valorForma.count_solicitacoes,
            filtros: filtrosForma,
            data_inicio: periodo.data_inicio,
            data_fim: periodo.data_fim,
          };

          setInfCards({
            conta,
            forma,

            recebidos: {
              count: recebidos.count,
              total: recebidos.total,
              count_solicitacoes: recebidos.count_solicitacoes,
              filtros: filtrosRecebidos,
              data_inicio: periodo.data_inicio,
              data_fim: periodo.data_fim,
            },

            total_receber: {
              count: totalReceber.count,
              total: totalReceber.total,
              count_solicitacoes: totalReceber.count_solicitacoes,
              filtros: filtrosReceita,
              data_inicio: periodo.data_inicio,
              data_fim: periodo.data_fim,
            },

            atrasados: {
              count: atrasados.count,
              total: atrasados.total,
              count_solicitacoes: atrasados.count_solicitacoes,
              filtros: filtrosAtrasados,
              data_inicio: periodoAtrasados?.data_inicio,
              data_fim: periodoAtrasados?.data_fim,
            },

            //Sem data inicial: o detalhamento apresenta o período "até" a data final
            atrasados_total: {
              count: atrasadosTotal.count,
              total: atrasadosTotal.total,
              count_solicitacoes: atrasadosTotal.count_solicitacoes,
              filtros: filtrosAtrasados,
              data_inicio: null,
              data_fim: dataFimAtrasadosTotal,
            },
          });
        } catch (error) {
          showMensagemErro(
            MENSAGENS_ERRO.TENTATIVA_OPERACAO,
            "carregar dashboard",
          );
        } finally {
          setLoading(false);
        }
      }

      loadDashboard();
    }
  }, [keyConsulta]);

  //### FUNÇÕES ###
  //Retorna as propriedades do detalhamento do card, para o componente CardDashboard apresentar o botão
  //@chaveCard: Chave do card em infCards. @coluna: Coluna (ou lista de colunas) a mais apresentada no detalhamento conforme o card
  function getPropsDetalhar(chaveCard, titulo, coluna, subtitulo = null) {
    return {
      onDetalhar: () =>
        handleBotaoDetalharCard(chaveCard, titulo, coluna, subtitulo),
      detalharDesabilitado: !(infCards[chaveCard].count > 0),
    };
  }

  //### EVENTOS ###
  //OnClick - Botão Detalhar do card (cada card tem o seu período do vencimento)
  function handleBotaoDetalharCard(chaveCard, titulo, coluna, subtitulo) {
    setCardDetalhe({
      ...infCards[chaveCard],
      titulo,
      subtitulo,
      coluna,
    });
  }

  return (
    <>
      {(loading && <SpinnerLoading mensagem="Carregando" />) || (
        <>
          {infCards != null && (
            <GridCards colunas={CARDS_POR_LINHA}>
              <CardDashboard
                titulo="ATRASADOS TOTAL"
                {...getPropsDetalhar(
                  "atrasados_total",
                  "Atrasados total",
                  COLUNA_DETALHE_CARD.VALOR_CONTA,
                )}
                valor1={`R$ ${fn_formatarNumero(infCards.atrasados_total.total, 2)}`}
                valor3={`${infCards.atrasados_total.count} ${infCards.atrasados_total.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.atrasados_total.count_solicitacoes} ${infCards.atrasados_total.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
                rodape="Aguardando data do pagamento"
                nomeIcone={
                  infCards.atrasados_total.count > 0 ? "ATENCAO" : "SUCESSO"
                }
              />

              <CardDashboard
                titulo="ATRASADOS DO PERÍODO"
                //subtitulo="Solicitações aprovadas"
                {...getPropsDetalhar(
                  "atrasados",
                  "Atrasados do período",
                  COLUNA_DETALHE_CARD.VALOR_CONTA,
                )}
                valor1={`R$ ${fn_formatarNumero(infCards.atrasados.total, 2)}`}
                valor3={`${infCards.atrasados.count} ${infCards.atrasados.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.atrasados.count_solicitacoes} ${infCards.atrasados.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
                rodape="Aguardando data do pagamento"
                nomeIcone={infCards.atrasados.count > 0 ? "ATENCAO" : "SUCESSO"}
              />

              {/* Sem o card da conta (grupo Receitas financeiras), o espaço dele fica vazio, para os demais cards
                  ficarem sempre na mesma posição e facilitar a comparação ao trocar o grupo */}
              {(infCards.conta && (
                <CardDashboard
                  titulo={infCards.conta.nome}
                  subtitulo="Pagamentos com posse"
                  {...getPropsDetalhar(
                    "conta",
                    infCards.conta.nome,
                    [
                      COLUNA_DETALHE_CARD.DATA_POSSE,
                      COLUNA_DETALHE_CARD.VALOR_CONTA,
                    ],
                    "Pagamentos com posse",
                  )}
                  valor1={`R$ ${fn_formatarNumero(infCards.conta.total, 2)}`}
                  valor3={`${infCards.conta.count} ${infCards.conta.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.conta.count_solicitacoes} ${infCards.conta.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
                  nomeIcone="CHECK"
                />
              )) || <div />}

              <CardDashboard
                titulo={infCards.forma.nome}
                subtitulo="Pagamentos com posse"
                {...getPropsDetalhar(
                  "forma",
                  infCards.forma.nome,
                  [
                    COLUNA_DETALHE_CARD.DATA_POSSE,
                    COLUNA_DETALHE_CARD.VALOR_CONTA,
                  ],
                  "Pagamentos com posse",
                )}
                valor1={`R$ ${fn_formatarNumero(infCards.forma.total, 2)}`}
                valor3={`${infCards.forma.count} ${infCards.forma.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.forma.count_solicitacoes} ${infCards.forma.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
                nomeIcone="CHECK"
              />

              <CardDashboard
                titulo="RECEBIDOS"
                {...getPropsDetalhar("recebidos", "Recebidos", [
                  COLUNA_DETALHE_CARD.DATA_PAGAMENTO,
                  COLUNA_DETALHE_CARD.VALOR_CONTA,
                ])}
                valor1={`R$ ${fn_formatarNumero(infCards.recebidos.total, 2)}`}
                valor3={`${infCards.recebidos.count} ${infCards.recebidos.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.recebidos.count_solicitacoes} ${infCards.recebidos.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
                nomeIcone="CHECK"
              />

              <CardDashboard
                titulo="TOTAL A RECEBER"
                {...getPropsDetalhar("total_receber", "Total a receber", [
                  COLUNA_DETALHE_CARD.DATA_PAGAMENTO,
                  COLUNA_DETALHE_CARD.VALOR_CONTA,
                ])}
                valor1={`R$ ${fn_formatarNumero(infCards.total_receber.total, 2)}`}
                valor3={`${infCards.total_receber.count} ${infCards.total_receber.count == 1 ? "pagamento" : "pagamentos"} / ${infCards.total_receber.count_solicitacoes} ${infCards.total_receber.count_solicitacoes == 1 ? "solicitação" : "solicitações"}`}
              />
            </GridCards>
          )}
        </>
      )}

      {/* MODAL DETALHAMENTO DO CARD */}
      {cardDetalhe && (
        <div>
          <TDashboard_detalhe
            modalOpen={cardDetalhe != null}
            toggleModal={() => setCardDetalhe(null)}
            card={cardDetalhe}
          />
        </div>
      )}
    </>
  );
}

export default TDashboard_rec;
